"use client";
import * as React from "react";
import { useState } from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { SectionTitle, SectionTitleProps } from "./SectionTitle";
import {
  ImageWithAspectRatioProps,
  CardBackgroundColorType,
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { sectionWrapper } from "./sectionWrappers";
import { VideoPlayerModal, WistiaPlayerConfig } from "./VideoPlayerModal";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { useElementHeight } from "./hooks/useElementHeight";

const cardFull = tv({
  base: cn(
    "uipm:flex uipm:h-full uipm:font-[var(--font-family-sans)]",
    // Mobile padding - using section-bottom for bottom padding, horizontal padding from container-margin
    "uipm:px-[var(--container-margin)] uipm:pt-[var(--container-card-large-padding)]",
    // Tablet padding and border radius
    "uipm:md:p-[var(--container-card-padding)] uipm:md:rounded-[var(--radius-radius-lg)]",
    // Desktop padding
    "uipm:lg:p-[var(--container-card-large-padding)]",
    // Mobile layout
    "uipm:flex-col uipm:gap-[var(--padding-padding-l)]",
    // Tablet/Desktop layout
    "uipm:md:flex-row uipm:md:rounded-[var(--radius-radius-lg)]",
    // Desktop gap
    "uipm:lg:gap-[var(--container-gutter)]",
  ),
  variants: {
    cardBackgroundColor: {
      primary: cn(
        "uipm:bg-[var(--surface-surface-accent)]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      secondary: cn(
        "uipm:bg-[var(--surface-surface-accent-secondary)]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      "secondary-light": cn(
        "uipm:bg-[var(--surface-surface-accent-light)]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      light: cn(
        "uipm:bg-[var(--surface-surface-secondary)]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      "very-light": cn(
        "uipm:bg-[#F7F7F7]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      white: cn(
        "uipm:bg-[var(--surface-surface-primary)]",
        "uipm:md:mx-0",
        "uipm:rounded-none",
      ),
      none: cn(
        "uipm:bg-transparent",
        // Remove top/bottom padding, keep horizontal padding
        "uipm:md:py-0 uipm:lg:py-0",
      ),
    },
    hasBackgroundImage: {
      true: cn(
        // Make card edge-to-edge on mobile when it has background
        // Restore normal margins on tablet+
        "uipm:md:mx-0",
        "uipm:rounded-none uipm:md:rounded-none",
      ),
      false: "",
    },
  },
  defaultVariants: {
    cardBackgroundColor: "primary",
    hasBackgroundImage: false,
  },
});

const contentSection = tv({
  base: cn(
    "uipm:flex uipm:flex-col",
    // Mobile: full width
    "uipm:w-full",
    // Tablet/Desktop: flex-1 for equal space
    "uipm:md:flex-1",
    "uipm:justify-center",
  ),
  variants: {
    orientation: {
      "image-right": cn(
        "uipm:md:order-1",
        // Tablet/Desktop: right padding when image is on right
        "uipm:md:pr-[var(--container-card-medium-padding)]",
        "uipm:lg:pr-[var(--container-card-medium-padding)]",
      ),
      "image-left": cn(
        "uipm:md:order-2",
        // Tablet/Desktop: left padding when image is on left
        "uipm:md:pl-[var(--container-card-medium-padding)]",
        "uipm:lg:pl-[var(--container-card-medium-padding)]",
      ),
    },
    typographyPosition: {
      top: "uipm:justify-start",
      center: "uipm:justify-center",
    },
  },
  defaultVariants: {
    orientation: "image-right",
    typographyPosition: "center",
  },
});

const imageWrapper = tv({
  base: "uipm:flex uipm:w-full uipm:md:flex-1",
  variants: {
    orientation: {
      "image-right": "uipm:md:order-2",
      "image-left": "uipm:md:order-1",
    },
    verticalAlignment: {
      top: "uipm:items-start",
      middle: "uipm:items-center",
      bottom: "uipm:items-end",
    },
  },
  defaultVariants: {
    orientation: "image-right",
    verticalAlignment: "middle",
  },
});

const imageSection = tv({
  base: cn(
    "uipm:relative uipm:flex-shrink-0",
    // Mobile: full width
    "uipm:w-full",
    // Desktop: fixed width 420px
  ),
  variants: {
    imageAlignment: {
      top: "uipm:items-start",
      middle: "uipm:items-center",
      bottom: "uipm:items-end",
    },
    imageHorizontalAlignment: {
      left: "uipm:justify-start",
      middle: "uipm:justify-center",
      right: "uipm:justify-end",
    },
    imageHorizontalAlignmentMobile: {
      left: "uipm:max-md:!justify-start",
      center: "uipm:max-md:!justify-center",
      right: "uipm:max-md:!justify-end",
    },
  },
  defaultVariants: {
    imageHorizontalAlignment: "middle",
    imageAlignment: "middle",
  },
});

export interface CardFullProps
  extends Omit<ComponentProps<"section">, "title"> {
  image?: ImageWithAspectRatioProps;
  video?: React.ReactNode;
  cardBackgroundImage?: ImageWithAspectRatioProps & {
    imageSpace?: "fill" | "fit";
  };
  sectionBackgroundImage?: ImageWithAspectRatioProps & {
    imageSpace?: "fill" | "fit";
  };
  orientation?: "image-right" | "image-left";
  typographyPosition?: "top" | "center";
  imageAlignment?: "top" | "middle" | "bottom";
  imageHorizontalAlignment?: "left" | "middle" | "right";
  sectionTitle?: SectionTitleProps;
  cardBackgroundColor?: CardBackgroundColorType;
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  videoId?: string;
  showVideoButton?: boolean;
  playButtonImage?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  videoConfig?: WistiaPlayerConfig;
  disableBottomPaddingOnMobile?: boolean;
}

export const CardFull: React.FC<CardFullProps> = ({
  image,
  video,
  cardBackgroundImage,
  sectionBackgroundImage,
  orientation = "image-right",
  typographyPosition = "top",
  imageAlignment = "middle",
  imageHorizontalAlignment = "middle",
  sectionTitle,
  cardBackgroundColor = "primary",
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  videoId,
  showVideoButton = false,
  playButtonImage,
  videoConfig,
  disableBottomPaddingOnMobile = false,
  className,
  id,
  ...props
}) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const { ref: contentSectionRef, height: contentSectionHeight } =
    useElementHeight<HTMLDivElement>();

  const handleVideoClick = () => {
    if (videoId) {
      setIsVideoModalOpen(true);
    }
  };

  const cardBgColorClassMap: Record<string, string> = {
    primary: "uipm:bg-[var(--surface-surface-accent)]",
    secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
    "secondary-light": "uipm:bg-[var(--surface-surface-accent-light)]",
    light: "uipm:bg-[var(--surface-surface-secondary)]",
    "very-light": "uipm:bg-[#F7F7F7]",
    white: "uipm:bg-[var(--surface-surface-primary)]",
    none: "uipm:bg-transparent",
  };

  return (
    <section
      id={id}
      className={cn(
        sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding: disableBottomPaddingOnMobile ? "none" : bottomPadding,
        }),
        sectionBackgroundImage && "uipm:relative",
      )}
      style={
        sectionBackgroundImage
          ? {
              backgroundImage: `url(${sectionBackgroundImage.focus ? `${sectionBackgroundImage.src}?focus=${sectionBackgroundImage.focus}` : sectionBackgroundImage.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }
          : undefined
      }
      {...props}
    >
      <div className={cn("uipm:max-w-[1440px] uipm:mx-auto")}>
        <div
          className={cn(
            cardFull({
              cardBackgroundColor,
              hasBackgroundImage: !!cardBackgroundImage,
            }),
            className,
            cardBackgroundImage && "uipm:relative uipm:overflow-hidden",
          )}
          style={
            cardBackgroundImage
              ? {
                  backgroundImage: `url(${cardBackgroundImage.focus ? `${cardBackgroundImage.src}?focus=${cardBackgroundImage.focus}` : cardBackgroundImage.src})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }
              : undefined
          }
        >
          {(image || video) && (
            <div
              className={cn(
                imageWrapper({
                  orientation,
                  verticalAlignment: imageAlignment,
                }),
                image?.aspectRatio === "1/1" && "uipm:min-h-[420px]",
                "uipm:max-md:min-h-fit",
              )}
            >
              <div
                className={cn(
                  imageSection({
                    imageAlignment,
                    imageHorizontalAlignment,
                    imageHorizontalAlignmentMobile:
                      image?.horizontalAlignmentMobile
                        ? (image.horizontalAlignmentMobile.toLowerCase() as
                            | "left"
                            | "center"
                            | "right")
                        : undefined,
                  }),
                  "uipm:w-full uipm:flex uipm:flex-row",
                  showVideoButton && "video-image-container",
                )}
              >
                {video ? (
                  video
                ) : image ? (
                  <ImageWithAspectRatio
                    {...image}
                    borderRadius="var(--radius-lg)"
                    showVideoButton={showVideoButton}
                    videoId={videoId}
                    onVideoClick={handleVideoClick}
                    playButtonImage={playButtonImage}
                    sizes={`${image.width ?? 420}px`}
                  />
                ) : null}
              </div>
            </div>
          )}

          {/* Content Section */}
          <div
            ref={contentSectionRef}
            className={contentSection({ orientation, typographyPosition })}
          >
            {sectionTitle && (
              <div className="uipm:w-full">
                <SectionTitle
                  {...sectionTitle}
                  // className="uipm:max-w-[700px]"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {disableBottomPaddingOnMobile && cardBackgroundColor !== "none" && (
        <div
          className={cn(
            "uipm:h-[var(--container-section-bottom)] uipm:md:hidden",
            cardBgColorClassMap[cardBackgroundColor as string],
          )}
        />
      )}

      {videoId && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoId={videoId}
          config={videoConfig}
        />
      )}
    </section>
  );
};

export type CardFullVariants = VariantProps<typeof cardFull>;
